// data table geofence for x-data-grid

import React, { useState, useCallback, useMemo } from "react";
import MUIDataTableImport from "mui-datatables";
import { IconButton, Stack, Tooltip } from "@mui/material";
import { Add, Delete, Forward, Rule } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { readTableRows, writeTableRows } from "../utils/tableStorage";

// Handle CJS/ESM interop - mui-datatables may export as default or as module object
const MUIDataTable = MUIDataTableImport.default || MUIDataTableImport;

const getSortInfo = (tableState) => ({
  name: tableState?.activeColumn || tableState?.sortOrder?.name,
  direction:
    tableState?.sortOrder?.direction ||
    (tableState?.announceText && tableState.announceText.includes("descending")
      ? "desc"
      : "asc"),
});

const DataTableBase = (props) => {
  const { storageKey } = props;

  const [rowsPerPage, setRowsPerPage] = useState(() => {
    if (storageKey) {
      const saved = readTableRows(storageKey);
      if (saved !== null) return saved;
    }
    return props.rowsPerPage || 10;
  });

  const { t } = useTranslation("common");

  // Destructure props to avoid dependency issues
  const {
    onFilterChange,
    onSortChange,
    setSelectedItems,
    setSelectedRows,
    data,
    actions,
    sortOrder,
    sortDirection,
    selectableRows,
    selectedRows,
    filterList,
  } = props;

  // Track current sort state internally, initialized from props
  const [currentSort, setCurrentSort] = useState(() => ({
    name: sortOrder || "name",
    direction: sortDirection || "asc",
  }));

  // Memoize the customToolbarSelect function to prevent re-creation on each render
  const customToolbarSelect = useCallback(() => {
    // if actions and actions.length > 0 then render buttons for each action
    if (actions && actions.length > 0) {
      return (
        <Stack direction="row" spacing={2} sx={{ mr: 2 }}>
          {actions.map((action) => {
            return (
              <Tooltip title={action.name} key={action.name}>
                <IconButton
                  onClick={() => {
                    action.action();
                  }}
                >
                  {action.icon === "Delete" && <Delete />}
                  {action.icon === "Forward" && <Forward />}
                  {action.icon === "Status" && <Rule />}
                  {action.icon === "Add" && <Add />}
                </IconButton>
              </Tooltip>
            );
          })}
        </Stack>
      );
    } else {
      return <div />;
    }
  }, [actions]);

  // Memoize the onRowSelectionChange handler
  const handleRowSelectionChange = useCallback(
    (rowsSelectedData, allRows, rowsSelected) => {
      let selectedRowID = [];
      if (rowsSelected.length > 0) {
        rowsSelected.forEach((row) => {
          if (data[row] && data[row].id) {
            selectedRowID.push(data[row].id);
          }
        });
      }
      setSelectedItems(selectedRowID);
      setSelectedRows(rowsSelected);
    },
    [data, setSelectedItems, setSelectedRows]
  );

  // Memoize the onTableChange handler
  const handleTableChange = useCallback(
    (action, newTableState) => {
      switch (action) {
        case "changeRowsPerPage":
          setRowsPerPage(newTableState.rowsPerPage);
          if (storageKey) {
            writeTableRows(storageKey, newTableState.rowsPerPage);
          }
          break;
        case "filterChange":
          // Call parent's onFilterChange if provided
          if (onFilterChange) {
            onFilterChange(newTableState.filterList);
          }
          break;
        case "columnSortChange":
        case "sort":
        case "sortChange":
        case "onColumnSortChange":
          // Call parent's onSortChange if provided
          if (onSortChange) {
            const sortInfo = getSortInfo(newTableState);
            setCurrentSort(sortInfo);
            onSortChange(sortInfo);
          }
          break;
        case "propsUpdate":
          break;
        default:
          // Try to detect sort changes in any action
          if (
            newTableState &&
            (newTableState.activeColumn || newTableState.sortOrder)
          ) {
            if (onSortChange) {
              const sortInfo = getSortInfo(newTableState);
              setCurrentSort(sortInfo);
              onSortChange(sortInfo);
            }
          }
          break;
      }
    },
    [onFilterChange, onSortChange, storageKey]
  );

  // Memoize the text labels to prevent re-creation on each render
  const textLabels = useMemo(
    () => ({
      body: {
        noMatch: t("common.no_records"),
        toolTip: t("common.sort"),
      },
      pagination: {
        next: t("common.next"),
        previous: t("common.previous"),
        rowsPerPage: t("common.rows_per_page"),
        displayRows: t("common.display_rows"),
      },
      toolbar: {
        search: t("common.search"),
        downloadCsv: t("common.download_csv"),
        viewColumns: t("common.view_columns"),
        filterTable: t("common.filter_table"),
      },
      filter: {
        title: t("common.title_filters"),
        reset: t("common.reset"),
      },
      viewColumns: {
        title: t("common.view_columns"),
      },
      selectedRows: {
        delete: t("common.delete"),
      },
    }),
    [t]
  );

  const handleColumnSortChange = useCallback(
    (columnName, direction) => {
      const sortInfo = { name: columnName, direction };
      setCurrentSort(sortInfo);
      if (onSortChange) {
        onSortChange(sortInfo);
      }
    },
    [onSortChange]
  );

  // Memoize the options object to prevent unnecessary re-renders
  const options = useMemo(() => {
    return {
      responsive: "standard",
      print: false,
      download: false,
      sortOrder: {
        name: currentSort.name || "name",
        direction: currentSort.direction || "asc",
      },
      selectableRows: selectableRows || "none",
      rowsPerPage: rowsPerPage,
      rowsPerPageOptions: [5, 10, 25, 50, 100],
      rowsSelected: selectedRows || [],
      filterList: filterList || [],
      customToolbarSelect,
      onTableChange: handleTableChange,
      onRowSelectionChange: handleRowSelectionChange,
      onColumnSortChange: handleColumnSortChange,
      textLabels,
    };
  }, [
    currentSort,
    selectableRows,
    rowsPerPage,
    selectedRows,
    filterList,
    customToolbarSelect,
    handleTableChange,
    handleRowSelectionChange,
    handleColumnSortChange,
    textLabels,
  ]);

  return (
    <MUIDataTable
      title={props.title}
      columns={props.columns}
      data={props.data}
      options={options}
    />
  );
};

// Wrap with React.memo to prevent unnecessary re-renders when props haven't changed
export default React.memo(DataTableBase);
